import React, { useState } from 'react';
import { Ban, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';

const MODES = {
  cancel: { title: 'Cancel', btn: 'Yes, cancel it', reasonRequired: false, tone: 'bg-red-600 hover:bg-red-700' },
  reopen: { title: 'Reopen as Draft', btn: 'Reopen as Draft', reasonRequired: true, tone: 'bg-orange-500 hover:bg-orange-600' },
  delete: { title: 'Delete', btn: 'Delete permanently', reasonRequired: false, tone: 'bg-red-600 hover:bg-red-700' },
};

const describe = (mode, label, number, status) => {
  if (mode === 'cancel') return `${number} will be marked Canceled. An Admin can reopen it later if this was a mistake.`;
  if (mode === 'reopen') return `${number} is ${status}. It goes back to Draft so it can be edited and issued again. The reason is kept in its history.`;
  return status === 'Draft' ? `${number} will be removed. This cannot be undone.` : `${number} is ${status}. Deleting removes this ${label} permanently. This cannot be undone.`;
};

// Cancel (with confirm) + Admin "Reopen as Draft" + Delete (Admin + reason for non-drafts).
export const DocOverrideActions = ({ docType, docId, label, number, status, cancelable, testPrefix, onAction, onReload, onDeleted }) => {
  const { api, user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const cfg = mode ? MODES[mode] : null;
  const needsReason = mode === 'reopen' || (mode === 'delete' && status !== 'Draft');
  const open = (m) => { setReason(''); setMode(m); };

  const run = async () => {
    if (needsReason && reason.trim().length < 3) { toast.error('Please enter a short reason'); return; }
    setBusy(true);
    try {
      if (mode === 'cancel') await onAction('cancel', { reason: reason.trim() });
      if (mode === 'reopen') { await api().post(`/${docType}/${docId}/reopen`, { reason: reason.trim() }); toast.success(`${number} reopened as Draft`); await onReload(); }
      if (mode === 'delete') { await api().delete(`/${docType}/${docId}`, { params: needsReason ? { reason: reason.trim() } : {} }); toast.success(`${number} deleted`); onDeleted(); }
      setMode(null);
    } catch (e) { toast.error(e.response?.data?.detail || `${cfg.title} failed`); } finally { setBusy(false); }
  };

  return (
    <>
      {cancelable && <Button size="sm" variant="outline" onClick={() => open('cancel')} className="border-red-200 text-red-700 hover:bg-red-50 rounded-sm" data-testid={`${testPrefix}-cancel-btn`}><Ban className="w-3.5 h-3.5 mr-1" /> Cancel</Button>}
      {isAdmin && status !== 'Draft' && <Button size="sm" variant="outline" onClick={() => open('reopen')} className="rounded-sm border-orange-300 text-orange-700 hover:bg-orange-50" data-testid={`${testPrefix}-reopen-btn`}><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen as Draft</Button>}
      {(status === 'Draft' || isAdmin) && <Button size="sm" variant="ghost" onClick={() => open('delete')} className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-sm" title={`Delete ${label}`} data-testid={`${testPrefix}-delete-btn`}><Trash2 className="w-3.5 h-3.5" /></Button>}
      <Dialog open={!!mode} onOpenChange={(o) => { if (!o) setMode(null); }}>
        {cfg && (
          <DialogContent data-testid={`${testPrefix}-${mode}-dialog`}>
            <DialogHeader>
              <DialogTitle>{cfg.title} {number}?</DialogTitle>
              <DialogDescription>{describe(mode, label, number, status)}</DialogDescription>
            </DialogHeader>
            {(mode === 'cancel' || needsReason) && (
              <div className="space-y-1">
                <Label>Reason{needsReason ? ' *' : ' (optional)'}</Label>
                <Textarea rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={mode === 'reopen' ? 'e.g. Cancelled by mistake' : 'e.g. Customer changed order'} data-testid={`${testPrefix}-${mode}-reason`} />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setMode(null)} data-testid={`${testPrefix}-${mode}-back`}>Go back</Button>
              <Button onClick={run} disabled={busy} className={cfg.tone} data-testid={`${testPrefix}-${mode}-confirm`}>{busy ? 'Working…' : cfg.btn}</Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};
